/**
 * Contacts surface (live) - the contact detail modal. True glass panel over a
 * blurred page. Restores the legacy anatomy on sx- materials, wired to real data:
 *   - Header: avatar, name, title/company, email (honest absence), LinkedIn link,
 *     met-at line (event/source + date met), inferred seniority/function tags.
 *   - Detected Signals: COLLAPSED by default (1a); each row links to the signal
 *     detail so the user can read it before outreach. Draft Outreach creates
 *     an editable, human-reviewed message grounded in that signal + contact.
 *   - Actions: a functional temperature selector (writes via update_contact), a
 *     Log Contact menu (email / phone / in person, via log_contact), and Edit.
 *   - Notes: editable, persisted via update_contact on blur.
 *   - Reminder: an editable date (+ optional time and reason) written via
 *     update_contact, with an .ics download and the calendar-delivery line. A
 *     past reminder reads "overdue" (stronger gold ink). Clearing is supported.
 *   - Remove at the bottom (destructive) behind a confirmation dialog.
 * A contact the viewer does not own (admin All scope) renders read-only.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { OutreachDraftControl } from "@/features/outreach/OutreachDraftControl";
import type { Contact, ContactLogMethod, ContactTemperature } from "../types";
import {
  TEMPERATURE_LABEL,
  formatDate,
  inferenceChips,
  initials,
  isFollowUpOverdue,
  metAtLine,
} from "../derive";
import { downloadIcs } from "../ics";
import { useDismiss } from "../use-dismiss";
import {
  IconChevron,
  IconClose,
  IconDownload,
  IconExternal,
  IconLinkedin,
  IconMail,
  IconPhone,
  IconPin,
  IconSignal,
  IconUser,
} from "../contact-icons";

const TEMP_OPTIONS: ContactTemperature[] = ["hot", "warm", "cold"];

const LOG_METHODS: { method: ContactLogMethod; label: string; icon: JSX.Element }[] = [
  { method: "email", label: "Email", icon: <IconMail /> },
  { method: "phone", label: "Phone", icon: <IconPhone /> },
  { method: "in_person", label: "In person", icon: <IconUser /> },
];

/** Normalize a stored LinkedIn value into an absolute, openable URL. */
function linkedinHref(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value.replace(/^\/+/, "")}`;
}

export function ContactDetailModal({
  contact,
  viewerId,
  isAdmin,
  onClose,
  onEdit,
  onRemove,
  onSaveNotes,
  onSetTemperature,
  onLogContact,
  onSaveReminder,
  onClearReminder,
  mutating,
  logging,
}: {
  contact: Contact;
  viewerId: string;
  isAdmin: boolean;
  onClose: () => void;
  onEdit: (contact: Contact) => void;
  onRemove: (contact: Contact) => void;
  onSaveNotes: (notes: string) => void;
  onSetTemperature: (temperature: ContactTemperature) => void;
  onLogContact: (method: ContactLogMethod) => void;
  onSaveReminder: (reminder: { date: string; time: string | null; reason: string | null }) => void;
  onClearReminder: () => void;
  mutating: boolean;
  logging: boolean;
}): JSX.Element {
  const [signalsOpen, setSignalsOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [notesDraft, setNotesDraft] = useState(contact.notes);
  const [logOpen, setLogOpen] = useState(false);
  const logRef = useDismiss<HTMLDivElement>(logOpen, setLogOpen);

  const [reminderDate, setReminderDate] = useState(contact.followUpDueAt ?? "");
  const [reminderTime, setReminderTime] = useState(contact.reminderTime ?? "");
  const [reminderReason, setReminderReason] = useState(contact.reminderReason ?? "");

  useEffect(() => {
    setNotesDraft(contact.notes);
    setReminderDate(contact.followUpDueAt ?? "");
    setReminderTime(contact.reminderTime ?? "");
    setReminderReason(contact.reminderReason ?? "");
  }, [
    contact.id,
    contact.notes,
    contact.followUpDueAt,
    contact.reminderTime,
    contact.reminderReason,
  ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (confirmRemove) setConfirmRemove(false);
      else onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, confirmRemove]);

  const owned = contact.ownerId === viewerId;
  const canDraft = owned || isAdmin;
  const chips = inferenceChips(contact.title);
  const signalCount = contact.matchedSignals.length;
  const metAt = metAtLine(contact);
  const overdue = isFollowUpOverdue(contact);

  const saveNotes = () => {
    if (owned && notesDraft !== contact.notes) onSaveNotes(notesDraft);
  };

  const reminderDirty =
    reminderDate !== (contact.followUpDueAt ?? "") ||
    reminderTime !== (contact.reminderTime ?? "") ||
    reminderReason !== (contact.reminderReason ?? "");
  const canSaveReminder = reminderDate !== "" && reminderDirty && !mutating;
  const saveReminder = () => {
    if (!canSaveReminder) return;
    onSaveReminder({
      date: reminderDate,
      time: reminderTime.trim() ? reminderTime : null,
      reason: reminderReason.trim() ? reminderReason : null,
    });
  };

  return (
    <div className="sxc-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="sxc-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Contact detail: ${contact.name || "contact"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="sxc-modal-close" aria-label="Close" onClick={onClose}>
          <IconClose />
        </button>

        <header className="sxc-m-header">
          <span className="sxc-m-avatar" aria-hidden="true">
            {initials(contact.name)}
          </span>
          <div className="sxc-m-headmain">
            <h2 className="sxc-m-name">{contact.name || "Unnamed contact"}</h2>
            <p className="sxc-m-title">
              {[contact.title, contact.company].filter(Boolean).join(" · ") ||
                "No title or company"}
            </p>
            <div className="sxc-m-contact">
              {contact.email ? (
                <a className="sxc-m-link" href={`mailto:${contact.email}`}>
                  <IconMail />
                  {contact.email}
                </a>
              ) : (
                <span className="sxc-m-empty">No email on file</span>
              )}
              {contact.linkedin ? (
                <a
                  className="sxc-m-link"
                  href={linkedinHref(contact.linkedin)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <IconLinkedin />
                  LinkedIn
                  <IconExternal />
                </a>
              ) : null}
            </div>
            {metAt ? (
              <p className="sxc-m-metat">
                <IconPin />
                {metAt}
              </p>
            ) : null}
            <div className="sxc-m-tags">
              {chips.map((chip) => (
                <span className="sxc-tag" key={chip}>
                  {chip}
                </span>
              ))}
              {contact.temperature ? (
                <span className={`sxc-temp sxc-temp--${contact.temperature}`}>
                  {TEMPERATURE_LABEL[contact.temperature]}
                </span>
              ) : (
                <span className="sxc-temp sxc-temp--unset" title="No temperature set">
                  —
                </span>
              )}
            </div>
          </div>
        </header>

        <section className="sxc-m-signalzone">
          {signalCount > 0 ? (
            <>
              <button
                type="button"
                className="sx-strip sxc-strip"
                aria-expanded={signalsOpen}
                onClick={() => setSignalsOpen((o) => !o)}
              >
                <span className="sx-s-label">
                  <IconSignal />
                  {signalCount} Detected Signal{signalCount === 1 ? "" : "s"}
                </span>
                <span
                  className={`sxc-strip-chevron${signalsOpen ? " sxc-strip-chevron--open" : ""}`}
                >
                  <IconChevron />
                </span>
              </button>
              {signalsOpen ? (
                <div className="sxc-signal-list">
                  {contact.matchedSignals.map((s) => (
                    <article className="sxc-signal" key={s.signalId}>
                      <div className="sxc-signal-head">
                        <div>
                          {s.company ? <p className="sxc-signal-co">{s.company}</p> : null}
                          {s.sourceUrl ? (
                            <a
                              className="sxc-signal-title sxc-signal-source"
                              href={s.sourceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`Open source article: ${s.headline}`}
                            >
                              {s.headline}
                              <IconExternal />
                            </a>
                          ) : (
                            <p className="sxc-signal-title">{s.headline}</p>
                          )}
                        </div>
                        {typeof s.score === "number" ? (
                          <div className="sxc-signal-score">
                            <span className="sxc-signal-val">{s.score}</span>
                            <span className="sxc-signal-den">/100</span>
                          </div>
                        ) : null}
                      </div>
                      <div className="sxc-signal-actions">
                        <Link className="sxc-signal-inapp" to={`/signals/${s.signalId}`}>
                          <IconSignal />
                          View in Signals
                        </Link>
                      </div>
                      <div className="sxc-outreach">
                        <OutreachDraftControl
                          signalId={s.signalId}
                          contactId={contact.id}
                          buttonClassName="sxc-draft-btn"
                          disabled={!canDraft}
                        />
                        <span className="sxc-outreach-note">
                          {canDraft
                            ? "Draft only — review before sending."
                            : `View only. This contact belongs to ${contact.ownerName}.`}
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <p className="sxc-m-empty sxc-nosignals">No detected signals for this contact.</p>
          )}
        </section>

        {owned ? (
          <section className="sxc-m-actions">
            <div className="sxc-temp-select" role="radiogroup" aria-label="Set temperature">
              <span className="sxc-temp-select-lbl">Temperature</span>
              {TEMP_OPTIONS.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={contact.temperature === t}
                  className={`sxc-temp-opt sxc-temp-opt--${t}${contact.temperature === t ? " sxc-temp-opt--on" : ""}`}
                  disabled={mutating}
                  onClick={() => {
                    if (contact.temperature !== t) onSetTemperature(t);
                  }}
                >
                  {TEMPERATURE_LABEL[t]}
                </button>
              ))}
            </div>

            <div className="sxc-logmenu" ref={logRef}>
              <button
                type="button"
                className="sx-btn sx-btn-p2"
                aria-expanded={logOpen}
                aria-haspopup="menu"
                disabled={logging}
                onClick={() => setLogOpen((o) => !o)}
              >
                {logging ? "Logging..." : "Log Contact"}
              </button>
              {logOpen ? (
                <div className="sxc-logmenu-list" role="menu">
                  {LOG_METHODS.map((m) => (
                    <button
                      key={m.method}
                      type="button"
                      role="menuitem"
                      className="sxc-logmenu-item"
                      onClick={() => {
                        setLogOpen(false);
                        onLogContact(m.method);
                      }}
                    >
                      {m.icon}
                      {m.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <button type="button" className="sx-btn sx-btn-p2" onClick={() => onEdit(contact)}>
              Edit
            </button>
          </section>
        ) : (
          <section className="sxc-m-actions">
            <span className="sxc-viewonly-note">
              View only. This contact belongs to {contact.ownerName}.
            </span>
          </section>
        )}
        {owned ? (
          <p className="sxc-helper sxc-action-note">
            {contact.temperature === null
              ? "Temperature not set."
              : contact.temperatureSource === "stored"
                ? "Temperature set by you."
                : "Temperature derived from matched-signal strength."}
            {contact.contactCount > 0 || contact.lastTouchAt
              ? ` Contacted ${contact.contactCount} time${contact.contactCount === 1 ? "" : "s"}${contact.lastTouchAt ? `, last on ${formatDate(contact.lastTouchAt)}` : ""}.`
              : ""}
          </p>
        ) : null}

        <section className="sxc-m-section">
          <p className="sx-eyebrow">Notes</p>
          {owned ? (
            <>
              <textarea
                className="sx-field sxc-notes"
                placeholder="Add notes about this contact..."
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                onBlur={saveNotes}
              />
              {mutating ? <span className="sxc-helper">Saving...</span> : null}
            </>
          ) : (
            <p className="sxc-readonly">{contact.notes || "No notes recorded."}</p>
          )}
        </section>

        <section className="sxc-m-section">
          <p className="sx-eyebrow">Reminder</p>
          {contact.followUpDueAt ? (
            <p className={`sxc-readonly${overdue ? " sxc-reminder-overdue" : ""}`}>
              {overdue
                ? `Follow-up overdue ${formatDate(contact.followUpDueAt)}`
                : `Reminder set: ${formatDate(contact.followUpDueAt)}`}
              {contact.reminderTime ? ` at ${contact.reminderTime}` : ""}
              {contact.reminderReason ? ` · ${contact.reminderReason}` : ""}
            </p>
          ) : (
            <p className="sxc-readonly">No reminder set.</p>
          )}

          {owned ? (
            <div className="sxc-reminder-edit">
              <div className="sxc-reminder-fields">
                <label className="sxc-field-wrap">
                  <span className="sx-eyebrow">Date</span>
                  <input
                    className="sx-field"
                    type="date"
                    value={reminderDate}
                    onChange={(e) => setReminderDate(e.target.value)}
                  />
                </label>
                <label className="sxc-field-wrap">
                  <span className="sx-eyebrow">Time (optional)</span>
                  <input
                    className="sx-field"
                    type="time"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                  />
                </label>
              </div>
              <label className="sxc-field-wrap">
                <span className="sx-eyebrow">Reason (optional)</span>
                <input
                  className="sx-field"
                  type="text"
                  placeholder="e.g. proposal follow-up"
                  value={reminderReason}
                  onChange={(e) => setReminderReason(e.target.value)}
                />
              </label>
              <div className="sxc-reminder-actions">
                <button
                  type="button"
                  className="sx-btn sx-btn-p2"
                  disabled={!canSaveReminder}
                  onClick={saveReminder}
                >
                  {contact.followUpDueAt ? "Update reminder" : "Set reminder"}
                </button>
                {contact.followUpDueAt ? (
                  <>
                    <button
                      type="button"
                      className="sx-btn sx-btn-p3"
                      disabled={mutating}
                      onClick={onClearReminder}
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      className="sx-btn sx-btn-p3 sxc-ics-btn"
                      onClick={() => downloadIcs(contact)}
                    >
                      <IconDownload /> .ics
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          ) : contact.followUpDueAt ? (
            <button
              type="button"
              className="sx-btn sx-btn-p2 sxc-ics-btn"
              onClick={() => downloadIcs(contact)}
            >
              <IconDownload /> Download .ics
            </button>
          ) : null}

          <p className="sxc-helper">
            Reminders are delivered via your calendar. Download the .ics to add it.
          </p>
        </section>

        {owned ? (
          <div className="sxc-modal-danger">
            <button
              type="button"
              className="sxc-remove-link"
              onClick={() => setConfirmRemove(true)}
            >
              Remove contact
            </button>
          </div>
        ) : null}

        {confirmRemove ? (
          <div
            className="sxc-confirm-overlay"
            role="presentation"
            onClick={() => setConfirmRemove(false)}
          >
            <div
              className="sxc-confirm-dialog"
              role="alertdialog"
              aria-modal="true"
              aria-label="Confirm removal"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="sxc-confirm-text">
                Remove {contact.name || "this contact"} from your contacts? This cannot be undone.
              </p>
              <div className="sxc-confirm-actions">
                <button
                  type="button"
                  className="sx-btn sx-btn-p3"
                  onClick={() => setConfirmRemove(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="sxc-confirm-remove"
                  onClick={() => onRemove(contact)}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
